import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Label as LabelIcon, Add as AddIcon } from '@mui/icons-material';
import { tagService } from '../services/api';
import TagChip from './TagChip';
import TagManager from './TagManager';

const EditFolderTagsDialog = ({ open, onClose, folder, onSave }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [error, setError] = useState('');
  const [tagManagerOpen, setTagManagerOpen] = useState(false);

  useEffect(() => {
    if (open) {
      loadAvailableTags();
    }
  }, [open]);

  useEffect(() => {
    if (folder && folder.tags) {
      // Se a pasta tem tags no formato novo (objetos com tag), extraia as tags
      const folderTags = folder.tags.map(folderTag =>
        folderTag.tag ? folderTag.tag : folderTag
      );
      setSelectedTags(folderTags);
    } else {
      setSelectedTags([]);
    }
  }, [folder, availableTags]);

  const loadAvailableTags = async () => {
    try {
      setLoadingTags(true);
      setError('');
      const response = await tagService.getAllTags();
      setAvailableTags(response.data || []);
    } catch (err) {
      setError('Erro ao carregar tags: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoadingTags(false);
    }
  };

  const handleSave = async () => {
    if (!folder) return;

    setLoading(true);
    try {
      // Remover todas as tags existentes da pasta
      if (folder.tags && folder.tags.length > 0) {
        for (const folderTag of folder.tags) {
          const tagId = folderTag.tag ? folderTag.tag.id : folderTag.id;
          await tagService.removeTagFromFolder(folder.id, tagId);
        }
      }

      // Adicionar as novas tags selecionadas
      for (const tag of selectedTags) {
        await tagService.addTagToFolder(folder.id, tag.id);
      }

      await onSave(); // Chamar callback para atualizar a lista
      onClose();
    } catch (error) {
      setError('Erro ao salvar tags: ' + (error.response?.data?.error || error.message));
      console.error('Error saving folder tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (folder && folder.tags) {
      const folderTags = folder.tags.map(folderTag =>
        folderTag.tag ? folderTag.tag : folderTag
      );
      setSelectedTags(folderTags);
    } else {
      setSelectedTags([]);
    }
    setError('');
    onClose();
  };

  const handleTagManagerClose = () => {
    setTagManagerOpen(false);
    loadAvailableTags(); // Recarregar tags após fechar o gerenciador
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Editar Tags - {folder?.name}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="body2" color="textSecondary">
              Selecione as tags para esta pasta:
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setTagManagerOpen(true)}
              size="small"
              variant="outlined"
            >
              Gerenciar Tags
            </Button>
          </Box>

          {loadingTags ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Autocomplete
              multiple
              value={selectedTags}
              onChange={(event, newValue) => setSelectedTags(newValue)}
              options={availableTags}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Selecione as tags..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <LabelIcon sx={{ color: 'action.active', mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <TagChip
                    key={option.id}
                    tag={option}
                    size="small"
                    onDelete={() => {
                      const newTags = selectedTags.filter(tag => tag.id !== option.id);
                      setSelectedTags(newTags);
                    }}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <TagChip tag={option} size="small" />
                  {option.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      - {option.description}
                    </Typography>
                  )}
                </Box>
              )}
              disabled={loading}
            />
          )}

          {selectedTags.length > 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {selectedTags.length} tag{selectedTags.length !== 1 ? 's' : ''} selecionada{selectedTags.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        <TagManager
          open={tagManagerOpen}
          onClose={handleTagManagerClose}
          onTagChange={loadAvailableTags}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditFolderTagsDialog;
